import { useState, useCallback, useRef, useEffect } from 'react';
import { DeviceType, SednaInfo } from './Device';
import log from 'app/home/general-components/Logging';

// static let validSourceDeviceTypes :[DeviceType] = [.door, .dpst, .lamp, .motion, .plug, .siren]
// static let validDestinationDeviceTypes :[DeviceType] = [.dpst, .lamp, .plug, .siren]

export default function useBindList(sendEvent) {
    const [binds, setBinds] = useState([])

    const halfOfMaxWaitingForChangeEnable = 1; //sec
    const setEnableHandler = useRef(undefined);
    const [needsCheckWaitingForChangeEnable, setNeedsCheckWaitingForChangeEnable] = useState(false);
    useEffect(() => {
        setEnableHandler.current = (id, enable) => {
            let newBinds = binds.map((bind) => {
                if (bind.id === id) {
                    const finalBind = { ...bind };
                    finalBind["enable"] = enable;
                    // if (!finalBind.hasOwnProperty('waitingForChangeEnable')) {
                    if (!Object.prototype.hasOwnProperty.call(finalBind, 'waitingForChangeEnable')) {
                        let now = new Date();
                        now.setSeconds(now.getSeconds() + halfOfMaxWaitingForChangeEnable);
                        finalBind['waitingForChangeEnable'] = {
                            time: now.getTime(),
                            ignoreWaiting: true,
                        }
                        setNeedsCheckWaitingForChangeEnable(true);
                    }
                    return finalBind;
                }
                return bind;
            })
            setBinds(newBinds);

            sendEvent('enable_bind', {
                id: id,
                enable: enable,
            });
        }
    }, [binds, sendEvent]);
    useEffect(() => {
        if (needsCheckWaitingForChangeEnable) {
            const check = () => {
                // console.log("new check");
                let now = (new Date()).getTime();
                let totallyChanged = false;
                let needsAnotherWaiting = false;
                let newBinds = binds.map((bind) => {
                    const finalBind = { ...bind };
                    if (finalBind['waitingForChangeEnable']) {
                        if (now >= finalBind['waitingForChangeEnable'].time) {
                            if (finalBind['waitingForChangeEnable'].ignoreWaiting !== false) {
                                finalBind['waitingForChangeEnable'].ignoreWaiting = false;
                                totallyChanged = true;
                                return finalBind;
                            }
                        } else {
                            needsAnotherWaiting = true;
                        }
                    }
                    return bind;
                });

                if (totallyChanged)
                    setBinds(newBinds);

                setNeedsCheckWaitingForChangeEnable(needsAnotherWaiting);
            };

            check();
            let t = setInterval(() => {
                check();
            }, halfOfMaxWaitingForChangeEnable * 1000);
            return () => {
                clearInterval(t);
            }
        }
    }, [binds, needsCheckWaitingForChangeEnable, halfOfMaxWaitingForChangeEnable]);

    const onBindRelatedEvent = useCallback((message) => {
        switch (message.ev) {
            case 'bind_list':
                let newBinds = [];
                for (let i = 0; i < message.msg.binds.length; i++) {
                    const bind = message.msg.binds[i];
                    newBinds.push({
                        ...bind,
                        setEnable: ((enable) => {
                            return setEnableHandler.current(bind.id, enable)
                        }),
                        remove: (() => {
                            sendEvent('remove_bind', {
                                id: bind.id,
                            });
                        }),
                        isDurationSpecified: bind.duration_from_time && bind.duration_end_time && bind.duration_enabled,
                    });
                }
                setBinds(newBinds);
                break;
            case 'enable_bind':
                let newBinds2 = binds.map((bind) => {
                    if (bind.id !== message.msg.id)
                        return bind;

                    const finalBind = { ...bind };
                    finalBind.enable = message.msg.enable;
                    if (finalBind.waitingForChangeEnable) {
                        delete finalBind["waitingForChangeEnable"];
                    }
                    return finalBind;
                })
                setBinds(newBinds2);
                break;
            case 'edit_bind':
                let newBinds4 = binds.map((bind) => {
                    if (bind.id !== message.msg.id)
                        return bind;

                    const finalBind = { ...message.msg };
                    finalBind.isDurationSpecified =
                        (finalBind.duration_from_time !== undefined) &&
                        (finalBind.duration_end_time !== undefined) &&
                        finalBind.duration_enabled;
                    finalBind.setEnable = ((enable) => {
                        return setEnableHandler.current(finalBind.id, enable)
                    })
                    finalBind.remove = (() => {
                        sendEvent('remove_bind', {
                            id: finalBind.id,
                        });
                    })
                    return finalBind;
                })
                setBinds(newBinds4);
                break;
            case 'remove_bind':
                let newBinds3 = [];
                for (let i = 0; i < binds.length; i++) {
                    const bind = binds[i];
                    if (bind.id !== message.msg.id)
                        newBinds3.push(bind);
                }
                setBinds(newBinds3);
                break;
            default:
                log('unsupported event in useBindList,', message.ev);
                break;
        }
    }, [sendEvent, binds])

    const createNewBind = useCallback((bind) => {
        sendEvent('new_bind', bind);
    }, [sendEvent]);

    const editBind = useCallback((bind) => {
        sendEvent('edit_bind', bind);
    }, [sendEvent]);

    return [binds, onBindRelatedEvent, createNewBind, editBind];
}

// export const BindRepeatType = {
//     always: 0,
//     once: 1,
// };

export const BindConditionType = {
    alarm: 0,
    device: 1,
};

export const BindActionType = {
    device: 0,
    notiftcation: 1,
    call: 2,
    email: 3,
    sms: 4,
    scenario: 5,
};

export function checkAndTransformDeviceForBind(negotiatedProtocol, stage, device) {
    if (stage === 'if') {
        switch (device.type) {
            case DeviceType.plug:
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
            case DeviceType.dpst:
            case DeviceType.siren:
            case DeviceType.motion:
            case DeviceType.doorSensor:
                return device;
            case DeviceType.wifiDoorSensor:
                return (negotiatedProtocol.hasWifiDoorSensor) ? device : null;
            case DeviceType.wifiMotionSensor:
                return (negotiatedProtocol.hasWifiMotionSensor) ? device : null;
            case DeviceType.multipurposeSensor:
                return (negotiatedProtocol.hasMultipurposeSensor) ? device : null;
            case DeviceType.lampX3:
                return (negotiatedProtocol.hasLampX3InBindAndScenario) ? device : null;
            case DeviceType.lampX4:
                return (negotiatedProtocol.hasLampX4InBind) ? device : null;
            case DeviceType.curtain1Lamp2:
                return (negotiatedProtocol.hasCurtain1Lamp2) ? device : null;
            case DeviceType.relay4:
                return (negotiatedProtocol.hasRelay4InBind) ? device : null;
            case DeviceType.curtain:
                return (negotiatedProtocol.hasCurtain) ? device : null;
            case DeviceType.sedna:
                return ((negotiatedProtocol.hasSednaInBind) && (device.sub_type === SednaInfo.subType.volkan)) ? device : null;
            case DeviceType.hinavaEcoThermostat:
                return ((negotiatedProtocol.hasEcoThermostat)) ? device : null;
            case DeviceType.evaporativeCooler:
                return (negotiatedProtocol.hasEvaporativeCoolerInBindAndScenario) ? device : null;
            case DeviceType.wc:
                return (negotiatedProtocol.hasWCInBindAndScenario) ? device : null;
            case DeviceType.elevator1:
                return (negotiatedProtocol.hasElevator1InBindAndScenario) ? device : null;
            case DeviceType.doorbell:
                return (negotiatedProtocol.hasDoorbellInBindAndScenario) ? device : null;
            default:
                return null;
        }
    } else {
        switch (device.type) {
            case DeviceType.plug:
            case DeviceType.lamp:
            case DeviceType.lampX1TwoWay:
            case DeviceType.dpst:
            case DeviceType.siren:
                return device;
            case DeviceType.lampX3:
                return (negotiatedProtocol.hasLampX3InBindAndScenario) ? device : null;
            case DeviceType.lampX4:
                return (negotiatedProtocol.hasLampX4InBind) ? device : null;
            case DeviceType.curtain1Lamp2:
                return (negotiatedProtocol.hasCurtain1Lamp2) ? device : null;
            case DeviceType.relay4:
                return (negotiatedProtocol.hasRelay4InBind) ? device : null;
            case DeviceType.curtain:
                return (negotiatedProtocol.hasCurtain) ? device : null;
            case DeviceType.sedna:
                return ((negotiatedProtocol.hasSednaInBind) && (device.sub_type === SednaInfo.subType.volkan)) ? device : null;
            case DeviceType.hinavaEcoThermostat:
                return ((negotiatedProtocol.hasEcoThermostat)) ? device : null;
            case DeviceType.remote:
            case DeviceType.tuyaRemote:
                let _device = { ...device };
                _device.sub_devices = _device.sub_devices.filter(d => checkAndTransformDeviceForBind(negotiatedProtocol, stage, d))
                return (negotiatedProtocol.hasRemoteInBindAndScenario) && (device.sub_devices.length > 0) ? _device : null;
            case DeviceType.tvRemote:
                return (negotiatedProtocol.hasRemoteInBindAndScenario) ? device : null;
            case DeviceType.splitRemote:
                if (device.config_per_mode)
                    return (negotiatedProtocol.hasRemoteInBindAndScenario) ? device : null;
                else
                    return null;
            case DeviceType.evaporativeCooler:
                return (negotiatedProtocol.hasEvaporativeCoolerInBindAndScenario) ? device : null;
            case DeviceType.wc:
                return (negotiatedProtocol.hasWCInBindAndScenario) ? device : null;
            case DeviceType.elevator1:
                return (negotiatedProtocol.hasElevator1InBindAndScenario) ? device : null;
            case DeviceType.doorbell:
                return (negotiatedProtocol.hasDoorbellInBindAndScenario) ? device : null;
            default:
                return null;
        }
    }
}
